import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  Close,
  ExpandLess,
  ExpandMore,
  Flag,
  HelpOutline,
  RadioButtonUnchecked,
  SkipNext,
} from '@mui/icons-material';
import {
  OnboardingStep,
  useGetOnboardingStateQuery,
  useUpdateOnboardingStateMutation,
} from '../../store/api/githubApi';

const CORE_STEPS = new Set(['install_github', 'connect_repo', 'run_scan', 'review_results']);

export const GettingStartedChecklist: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading } = useGetOnboardingStateQuery(undefined, {
    pollingInterval: 10000,
  });
  const [updateOnboarding] = useUpdateOnboardingStateMutation();
  const [expanded, setExpanded] = useState(true);
  const [manuallyOpened, setManuallyOpened] = useState(false);

  const visibleSteps = useMemo(
    () => (data?.steps ?? []).filter((step) => step.state !== 'dismissed'),
    [data?.steps]
  );
  const coreSteps = visibleSteps.filter((step) => CORE_STEPS.has(step.key));
  const completedCore = coreSteps.filter((step) => step.state === 'complete').length;
  const progress = coreSteps.length ? Math.round((completedCore / coreSteps.length) * 100) : 0;
  const activeStep = visibleSteps.find((step) => step.state === 'current')
    ?? visibleSteps.find((step) => step.state === 'optional');

  if (isLoading || !data) return null;

  const showChecklist = manuallyOpened || (data.status === 'active' && Boolean(activeStep));

  const handleOpenGuide = () => {
    setExpanded(true);
    setManuallyOpened(true);
    if (data.status !== 'active') {
      updateOnboarding({ status: 'active' });
    }
  };

  if (!showChecklist) {
    return (
      <Button
        variant="contained"
        startIcon={<HelpOutline sx={{ fontSize: 18 }} />}
        onClick={handleOpenGuide}
        sx={{
          position: 'fixed',
          right: { xs: 12, sm: 24 },
          bottom: { xs: 12, sm: 24 },
          zIndex: 1300,
          borderRadius: '999px',
          px: 2,
          py: 1.1,
          textTransform: 'none',
          fontWeight: 800,
          bgcolor: '#0f172a',
          color: '#ffffff',
          boxShadow: '0 14px 34px rgba(15, 23, 42, 0.22)',
          '&:hover': {
            bgcolor: '#1e293b',
            boxShadow: '0 16px 38px rgba(15, 23, 42, 0.28)',
          },
        }}
      >
        Guide me
      </Button>
    );
  }

  const isContextual = Boolean(activeStep && (
    activeStep.route === location.pathname
    || (activeStep.key === 'review_results' && location.pathname.startsWith('/scans/'))
  ));

  const handleStepClick = async (step: OnboardingStep) => {
    await updateOnboarding({ last_step_key: step.key }).unwrap();
    if (step.route && step.route !== location.pathname) {
      navigate(step.route);
    }
  };

  const handleSkip = () => {
    setManuallyOpened(false);
    updateOnboarding({ status: 'skipped' });
  };

  const handleDismissStep = () => {
    if (activeStep) {
      updateOnboarding({ dismissed_step: activeStep.key });
    } else {
      setManuallyOpened(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'fixed',
        right: { xs: 12, sm: 24 },
        bottom: { xs: 12, sm: 24 },
        width: { xs: 'calc(100vw - 24px)', sm: 380 },
        zIndex: 1300,
        border: '1px solid #dbeafe',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 18px 50px rgba(15, 23, 42, 0.16)',
        bgcolor: 'rgba(255, 255, 255, 0.96)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
      }}
    >
      <Box sx={{ p: 1.5, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Flag sx={{ fontSize: 18, color: '#2563eb' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>
              Getting started
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
              {completedCore} of {coreSteps.length} core steps complete
            </Typography>
          </Box>
          <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
            <IconButton size="small" onClick={() => setExpanded((value) => !value)}>
              {expanded ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Skip onboarding">
            <IconButton size="small" onClick={handleSkip}>
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            mt: 1.25,
            height: 6,
            borderRadius: 999,
            bgcolor: '#e2e8f0',
            '& .MuiLinearProgress-bar': { borderRadius: 999 },
          }}
        />
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          {activeStep && isContextual && activeStep.state === 'current' && (
            <Box
              sx={{
                mb: 1.25,
                p: 1.25,
                borderRadius: '8px',
                bgcolor: '#eff6ff',
                border: '1px solid #bfdbfe',
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#1d4ed8' }}>
                Next up: {activeStep.label}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#334155', mt: 0.35 }}>
                {activeStep.description}
              </Typography>
            </Box>
          )}

          {activeStep ? (
            <Stack spacing={0.75}>
            {visibleSteps.map((step) => {
              const isComplete = step.state === 'complete';
              const isActive = step.key === activeStep.key;
              const isLocked = step.state === 'locked';
              return (
                <Button
                  key={step.key}
                  fullWidth
                  disabled={isLocked}
                  onClick={() => handleStepClick(step)}
                  sx={{
                    justifyContent: 'flex-start',
                    gap: 1,
                    p: 1,
                    borderRadius: '8px',
                    textTransform: 'none',
                    color: isLocked ? '#94a3b8' : '#0f172a',
                    bgcolor: isActive ? '#f8fafc' : 'transparent',
                    border: isActive ? '1px solid #dbeafe' : '1px solid transparent',
                    '&:hover': { bgcolor: '#f8fafc' },
                  }}
                >
                  {isComplete ? (
                    <CheckCircle sx={{ fontSize: 18, color: '#16a34a', flexShrink: 0 }} />
                  ) : (
                    <RadioButtonUnchecked sx={{ fontSize: 18, color: isActive ? '#2563eb' : '#cbd5e1', flexShrink: 0 }} />
                  )}
                  <Box sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, lineHeight: 1.2 }}>
                      {step.label}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#64748b', lineHeight: 1.25, mt: 0.25 }}>
                      {step.description}
                    </Typography>
                  </Box>
                  {step.state === 'optional' && (
                    <Chip label="Optional" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
                  )}
                </Button>
              );
            })}
            </Stack>
          ) : (
            <Box
              sx={{
                p: 1.25,
                borderRadius: '8px',
                bgcolor: '#f8fafc',
                border: '1px solid #e2e8f0',
              }}
            >
              <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#0f172a' }}>
                You are set up.
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.35 }}>
                Use the shortcuts below when you need help navigating KodeGauge.
              </Typography>
              <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                {[
                  { label: 'View dashboard', route: '/dashboard' },
                  { label: 'Manage repositories', route: '/repositories' },
                  { label: 'Plan remediation', route: '/remediation-planner' },
                  { label: 'Configure policies', route: '/policies' },
                ].map((item) => (
                  <Button
                    key={item.route}
                    fullWidth
                    onClick={() => navigate(item.route)}
                    sx={{
                      justifyContent: 'flex-start',
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontSize: 12.5,
                      fontWeight: 800,
                      color: '#0f172a',
                      bgcolor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      '&:hover': { bgcolor: '#eff6ff', borderColor: '#bfdbfe' },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Stack>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 1.25 }}>
            <Button
              size="small"
              startIcon={<SkipNext sx={{ fontSize: 15 }} />}
              onClick={handleDismissStep}
              sx={{ textTransform: 'none', fontWeight: 700, color: '#64748b' }}
            >
              {activeStep ? 'Dismiss step' : 'Hide guide'}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => (activeStep ? handleStepClick(activeStep) : navigate('/dashboard'))}
              sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '8px' }}
            >
              {activeStep ? 'Continue' : 'Dashboard'}
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};
